import { useCallback, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { getRacks } from '../../services'
import { Rack } from '../../types/response'
import { paths } from '@/utils/path'
import { Badge } from '@/components/ui/badge'
import {
    DataTableCustomized,
    DataTableColumn,
} from '@/components/ui/data-table-customized'
import Paper from '@/components/Paper'
import { formatDate } from '../../utils'

const RACK_STATUS_VARIANTS: Record<string, string> = {
    active: 'default',
    available: 'secondary',
    reserved: 'outline',
    planned: 'outline',
    deprecated: 'destructive',
    decommissioning: 'destructive',
}

const rackColumns: DataTableColumn<Rack>[] = [
    {
        key: 'name',
        title: 'Name',
        width: 200,
        pinned: 'left',
        render: (_value, row) => (
            <Link
                to={`${paths.warehouseRacks}/${row.id}`}
                className="text-blue-600 hover:underline font-medium"
            >
                {row.name}
            </Link>
        ),
    },
    {
        key: 'code',
        title: 'Code',
        width: 120,
        render: (value) => (
            <span className="text-sm font-medium">{value as string}</span>
        ),
    },
    {
        key: 'status',
        title: 'Status',
        width: 130,
        render: (_value, row) => (
            <Badge
                variant={
                    (RACK_STATUS_VARIANTS[row.status] as
                        | 'default'
                        | 'secondary'
                        | 'outline'
                        | 'destructive') || 'outline'
                }
                className="capitalize"
            >
                {row.status}
            </Badge>
        ),
    },
    {
        key: 'u_height',
        title: 'U Height',
        width: 90,
        render: (value) => <span className="text-sm">{value as number}U</span>,
    },
    {
        key: 'power_supply',
        title: 'Power (W)',
        width: 100,
        render: (value) => (
            <span className="text-sm">{(value as number) || 0}</span>
        ),
    },
    {
        key: 'bandwidth_limit',
        title: 'BW Limit',
        width: 110,
        render: (value) => (
            <span className="text-sm">{(value as number) || 0} Mbps</span>
        ),
    },
    {
        key: 'bandwidth_reality',
        title: 'BW Usage',
        width: 110,
        render: (value) => (
            <span className="text-sm">{(value as number) || 0} Mbps</span>
        ),
    },
    {
        key: 'started_at',
        title: 'Started',
        width: 120,
        render: (value) => (
            <span className="text-sm">{formatDate(value as string)}</span>
        ),
    },
]

const LocationRacks = () => {
    const { id } = useParams<{ id: string }>()

    const { params, queryParams, setParams, searchInputProps } =
        useUrlSearchParams({
            prefix: 'rack',
            defaults: { sort_by: 'name', sort_order: 'asc' },
        })

    const fetchFn = useCallback(
        () => getRacks({ ...queryParams, location_id: id }),
        [queryParams, id]
    )

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_RACKS, 'location', id, queryParams],
        queryFn: fetchFn,
        placeholderData: true,
    })

    const racks = data?.results || []
    const pagination = data?.pagination

    const columns = useMemo(() => rackColumns, [])

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return (
        <Paper className="mt-2 space-y-2">
            {renderState() || (
                <DataTableCustomized
                    columns={columns}
                    rows={racks}
                    rowKey={(row) => row.id}
                    size="small"
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: 'Search racks...',
                        },
                    }}
                    sorting={{
                        sortBy: params.sort_by,
                        sortOrder: params.sort_order,
                        onSortChange: handleSortChange,
                    }}
                    pagination={{
                        page: params.page,
                        pageSize: params.page_size,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                    }}
                />
            )}
        </Paper>
    )
}

export default LocationRacks
